'use client';

import { Button } from '@/components/ui/button';
import { useGWPDiscount } from '@/hooks/use-gwp-discount';
import { useGWPRemovalTracking } from '@/hooks/use-gwp-removal-tracking';
import { useAddToCart, useProductInventory } from '@veranda/shared/cart/hooks';
import { useCart, useCartTotals } from '@veranda/shared/cart/provider';
import type { ContextCartItem } from '@veranda/shared/cart/types';
import { adaptProductCardToCart } from '@veranda/shared/cart/utils';
import { cn } from '@veranda/shared/lib';
import { useMaxQuantity } from '@veranda/shared/lib/hooks';
import { SanityImage } from '@veranda/shared/sanity/utils/components';
import { Check } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';

interface GWPCountdownProps {
  discountId?: string;
  className?: string;
}

export function GWPCountdown({ discountId, className }: GWPCountdownProps) {
  const { cart, loading } = useCart();
  const totals = useCartTotals();
  const { addItem } = useAddToCart({ domain: 'tilecloud.com' });
  const { isGWPManuallyRemoved, clearGWPRemoval, markGWPAsRemoved } =
    useGWPRemovalTracking();
  const hasAutoAddedRef = useRef(false);
  const prevCartItemsRef = useRef<ContextCartItem[] | undefined>(undefined);

  const gwpQuery = useGWPDiscount(discountId);

  const currentTotal = Number(totals.amount);
  const gwpData = gwpQuery.data?.data;

  const inventory = useProductInventory(gwpData?.product.productGid || null);

  // Get max quantity for the GWP product
  const maxQuantityAvailable = useMaxQuantity({
    productGid: gwpData?.product.productGid ?? null,
    variantGid: gwpData?.product.variantGid ?? null,
    allowBackorder: gwpData?.product.allowBackorder ?? false,
  });

  const calculations = useMemo(() => {
    if (!gwpData) return null;

    const progress = Math.min((currentTotal / gwpData.threshold) * 100, 100);
    const amountRemaining = Math.max(gwpData.threshold - currentTotal, 0);
    const isThresholdMet = currentTotal >= gwpData.threshold;

    let isAlreadyInCart = false;

    if (
      cart?.items &&
      Array.isArray(cart.items) &&
      gwpData.product?.variantGid
    ) {
      isAlreadyInCart = cart.items.some(
        (item) => item?.variantId === gwpData.product.variantGid
      );
    }

    const canAddToCart =
      isThresholdMet &&
      !inventory.isOutOfStock &&
      !isAlreadyInCart &&
      gwpData.isActive;

    const isOutOfStock =
      inventory.isOutOfStock || inventory.data?.availableForSale === false;

    return {
      progress,
      amountRemaining,
      isThresholdMet,
      formattedAmountRemaining: `$${amountRemaining.toFixed(2)}`,
      canAddToCart,
      isAlreadyInCart,
      isOutOfStock,
    };
  }, [currentTotal, gwpData, cart?.items, inventory]);

  // Track when GWP items are removed from cart
  useEffect(() => {
    if (!gwpData?.product?.variantGid || !cart?.items) {
      prevCartItemsRef.current = cart?.items;
      return;
    }

    if (prevCartItemsRef.current) {
      const wasInCart = prevCartItemsRef.current.some(
        (item: ContextCartItem) => item.variantId === gwpData.product.variantGid
      );

      const isInCart = cart.items.some(
        (item: ContextCartItem) => item.variantId === gwpData.product.variantGid
      );

      // If it was in cart but now it's not, and it's a GWP item, mark as manually removed
      if (wasInCart && !isInCart) {
        const removedItem = prevCartItemsRef.current.find(
          (item) => item.variantId === gwpData.product.variantGid
        );

        // Check if it was a free gift
        if (removedItem?.isFreeGift) {
          markGWPAsRemoved(gwpData.product.variantGid);
        }
      }
    }

    prevCartItemsRef.current = cart.items;
  }, [cart?.items, gwpData?.product?.variantGid, markGWPAsRemoved]);

  // Auto-add GWP when threshold is met
  useEffect(() => {
    // Don't auto-add while cart is loading (prevents race conditions)
    if (
      !loading &&
      calculations?.isThresholdMet &&
      !calculations?.isAlreadyInCart &&
      !calculations?.isOutOfStock &&
      gwpData?.isActive &&
      gwpData?.product?.variantGid &&
      !hasAutoAddedRef.current &&
      !isGWPManuallyRemoved(gwpData.product.variantGid)
    ) {
      const cartData = adaptProductCardToCart(gwpData.product);
      if (cartData) {
        const { product, variant } = cartData;

        hasAutoAddedRef.current = true;

        addItem({
          product,
          variant,
          quantity: 1,
          maxQuantityAvailable,
          isFreeGift: true,
        });
      }
    }

    // Reset the auto-add flag when threshold is no longer met
    if (!calculations?.isThresholdMet) {
      hasAutoAddedRef.current = false;
    }
  }, [
    loading,
    calculations?.isThresholdMet,
    calculations?.isAlreadyInCart,
    calculations?.isOutOfStock,
    gwpData,
    addItem,
    maxQuantityAvailable,
    isGWPManuallyRemoved,
  ]);

  // Don't render if cart is empty, no GWP data, loading, product is out of stock, or discount is not active
  if (
    !cart?.items?.length ||
    !gwpQuery.data?.success ||
    !gwpData ||
    !gwpData.isActive ||
    calculations?.isOutOfStock
  ) {
    return null;
  }

  const handleAddToCart = () => {
    if (!calculations?.canAddToCart || !gwpData?.product) return;

    const cartData = adaptProductCardToCart(gwpData.product);
    if (!cartData) return;

    const { product, variant } = cartData;

    if (gwpData.product.variantGid) {
      clearGWPRemoval(gwpData.product.variantGid);
    }

    addItem({
      product,
      variant,
      quantity: 1,
      maxQuantityAvailable,
      isFreeGift: true,
    });
  };

  const getButtonContent = () => {
    if (calculations?.isAlreadyInCart) {
      return (
        <>
          <Check className="h-4 w-4" />
          Added to Cart
        </>
      );
    }

    if (!calculations?.isThresholdMet) {
      return '🔒 UNLOCK FREE GIFT';
    }

    return '🔒 UNLOCK FREE GIFT';
  };

  const message = calculations?.isThresholdMet
    ? `🎉 You've unlocked your gift: ${gwpData.product.title}!`
    : `You're ${calculations?.formattedAmountRemaining} away from unlocking your gift!`;

  return (
    <div
      className={cn('mb-4 bg-brand-beige px-6 py-3', className)}
      style={{
        ['--gwp-text-color' as string]: 'var(--color-brand-burgundy)',
        ['--gwp-track-color' as string]: 'rgba(97, 41, 39, 0.2)',
        ['--gwp-progress-color' as string]: 'var(--color-brand-burgundy)',
      }}
    >
      <div className="mb-2 text-left font-medium text-brand-burgundy text-sm">
        {message}
      </div>
      <div className="relative h-2 overflow-hidden border border-brand-burgundy/30 bg-white/40">
        <div
          className="relative h-full bg-gradient-to-r from-brand-burgundy to-brand-burgundy/85 transition-all duration-700 ease-out"
          style={{ width: `${calculations?.progress}%` }}
        >
          <div
            className="absolute inset-0 opacity-40"
            style={{
              background: `linear-gradient(90deg, 
                transparent, 
                rgba(255, 255, 255, 0.2) 20%, 
                transparent 40%, 
                rgba(255, 255, 255, 0.2) 60%, 
                transparent 80%,
                rgba(255, 255, 255, 0.2)
              )`,
              backgroundSize: '200% 100%',
              animation: 'shipping-progress-wave 3s ease-in-out infinite',
            }}
          />
          <div
            className="absolute inset-0 bg-white/10"
            style={{
              animation: 'shipping-progress-breathe 4s ease-in-out infinite',
            }}
          />
        </div>
      </div>
      <article
        className={cn(
          'mt-2 flex items-start py-3 text-[hsl(var(--color))] transition-all duration-300'
        )}
      >
        {gwpData.product.mainImage && (
          <div className="relative size-24">
            <SanityImage
              image={gwpData.product.mainImage}
              fill
              sizes="100px"
              className="absolute h-full w-full object-cover"
            />
          </div>
        )}

        <main className="ml-5 flex flex-1 flex-col">
          <h3 className="font-bold text-sm uppercase">
            {gwpData.product.title}
          </h3>
          <h4 className="text-xs">
            {calculations?.isThresholdMet
              ? 'FREE with your order!'
              : `FREE when you spend $${gwpData.threshold}`}
          </h4>
          <div className="mt-2 flex xs:items-center gap-2 max-xs:flex-col max-xs:items-start">
            <Button
              size="sm"
              onClick={handleAddToCart}
              disabled={
                !calculations?.canAddToCart || calculations?.isAlreadyInCart
              }
              className={cn('gap-2')}
            >
              {getButtonContent()}
            </Button>
          </div>
        </main>
      </article>
    </div>
  );
}
